'use client'

import { useState } from 'react'
import { FadingScrollable } from '../../../elements/FadingScrollable'
import { Timestamp } from './audio/audio-elements'
import { twMerge } from 'tailwind-merge'

interface PreviewableListItem {
  id: string
  title: string
  summary?: string
  transcript?: string
  startedAt?: string
  createdAt?: string
}

interface PreviewableListProps {
  results: PreviewableListItem[]
  className?: string
  disableScrollable?: boolean
  disableClick?: boolean
}

const scrollablePreviewClassName = twMerge(
  'font-350 w-full flex max-h-48 flex-col gap-3 p-3 py-2 text-[13px] bg-transparent',
)
const noScrollPreviewClassName = twMerge('font-350 w-full flex flex-col gap-3 p-3 py-2 text-[13px] bg-transparent')
const scrollableAreaClassName = twMerge(
  'text-light/50 flex h-full flex-col gap-2 text-ellipsis whitespace-pre-line leading-normal bg-transparent',
)
const noScrollAreaClassName = twMerge(
  'text-light/50 flex flex-col gap-2 text-ellipsis whitespace-pre-line leading-normal bg-transparent',
)

export const PreviewableList = ({
  results,
  className,
  disableScrollable = false,
  disableClick = false,
}: PreviewableListProps) => {
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set())

  const toggleExpanded = (index: number) => {
    if (disableClick) return
    setExpandedItems((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(index)) {
        newSet.delete(index)
      } else {
        newSet.add(index)
      }
      return newSet
    })
  }

  const renderItems = () => (
    <div className="bg-transparent py-2">
      {results.map((note, index) => {
        const isExpanded = expandedItems.has(index)
        return (
          <div key={note.id} className="bg-transparent">
            <div className={'flex flex-col gap-1 bg-transparent py-1'}>
              <span
                className={
                  disableClick
                    ? 'text-light text-[12px] leading-tight'
                    : 'text-light cursor-pointer text-[12px] leading-tight underline-offset-2 transition-colors hover:text-white hover:underline'
                }
                onClick={
                  disableClick
                    ? undefined
                    : (e) => {
                        e.stopPropagation()
                        toggleExpanded(index)
                      }
                }
              >
                {note.title}
              </span>
              <Timestamp
                className={'font-350 text-light/30 text-[10px] leading-normal'}
                date={note.startedAt || note.createdAt || new Date().toISOString()}
              />
            </div>
            {!disableClick && isExpanded && (
              <div className="bg-light-5 mt-2 rounded-lg px-3">
                <FadingScrollable className="text-light/70 max-h-32 whitespace-pre-line text-[12px] leading-relaxed">
                  <div className="pt-2">{note.transcript || note.summary || 'No transcript available'}</div>
                </FadingScrollable>
              </div>
            )}
            {index < results.length - 1 && <div className={'bg-light/10 my-2 h-px w-full'} />}
          </div>
        )
      })}
    </div>
  )

  return (
    <div className={twMerge(disableScrollable ? noScrollPreviewClassName : scrollablePreviewClassName, className)}>
      {disableScrollable ? (
        <div className={twMerge(noScrollAreaClassName, 'pb-2')} onClick={(e) => e.stopPropagation()}>
          {renderItems()}
        </div>
      ) : (
        <FadingScrollable className={twMerge(scrollableAreaClassName, 'pb-2')} onClick={(e) => e.stopPropagation()}>
          {renderItems()}
        </FadingScrollable>
      )}
    </div>
  )
}
